/**
 * Frontend Integration Registry
 * 
 * This registry contains all integration definitions for the frontend.
 * It serves as the single source of truth for integration metadata,
 * component references, and configuration.
 */

import { IntegrationRegistry, IntegrationDefinition } from '../types';
// Brand icons are now referenced by string names instead of imports

// Import integration components
import ClaudeDesktopIntegrationModal from '../../components/integrations/claude/ClaudeDesktopIntegrationModal';
import { ClaudeDesktopSuccessModal } from '../../components/integrations/claude/ClaudeDesktopSuccessModal';
import CustomGPTIntegrationModal from '../../components/integrations/openai/CustomGPTIntegrationModal';
import { CustomGPTSuccessModal } from '../../components/integrations/openai/CustomGPTSuccessModal';
import BareAPIIntegrationModal from '../../components/integrations/bare_api/BareAPIIntegrationModal';
import { BareAPISuccessModal } from '../../components/integrations/bare_api/BareAPISuccessModal';
import { BareAPIConfigModal } from '../../components/integrations/bare_api/BareAPIConfigModal';
import BareMCPIntegrationModal from '../../components/integrations/bare_mcp/BareMCPIntegrationModal';
import { BareMCPSuccessModal } from '../../components/integrations/bare_mcp/BareMCPSuccessModal';
import CursorIntegrationModal from '../../components/integrations/cursor/CursorIntegrationModal';
import { CursorSuccessModal } from '../../components/integrations/cursor/CursorSuccessModal';
import { WhatsAppIntegrationModal } from '../../components/integrations/whatsapp/WhatsAppIntegrationModal';
import N8NIntegrationModal from '../../components/integrations/n8n/N8NIntegrationModal';
import { N8NSuccessModal } from '../../components/integrations/n8n/N8NSuccessModal';
import DifyIntegrationModal from '../../components/integrations/dify/DifyIntegrationModal';
import { DifySuccessModal } from '../../components/integrations/dify/DifySuccessModal';
import ZapierIntegrationModal from '../../components/integrations/zapier/ZapierIntegrationModal';
import { ZapierSuccessModal } from '../../components/integrations/zapier/ZapierSuccessModal';
import ChromeExtensionIntegrationModal from '../../components/integrations/chrome/ChromeExtensionIntegrationModal';
import { ChromeExtensionSuccessModal } from '../../components/integrations/chrome/ChromeExtensionSuccessModal';
import ExtensionBuilderModal from '../../components/integrations/chrome/ExtensionBuilderModal';
import { XBookmarksIntegrationModal } from '../../components/integrations/x_bookmarks/XBookmarksIntegrationModal';
import { XBookmarksSuccessModalContent } from '../../components/integrations/x_bookmarks/XBookmarksSuccessModal';

/**
 * Main integration registry
 * 
 * Populated with all available integrations and their component mappings.
 */
export const integrationRegistry: IntegrationRegistry = {
  claude_desktop: {
    id: 'claude_desktop',
    name: 'Claude (Desktop & web)',
    category: 'ai_assistants',
    socketTypes: ['mcp'],
    status: 'available',
    icon: 'anthropic',
    gradient: 'from-purple-500 to-indigo-600',
    description: 'Connect Claude (Desktop or claude.ai connectors) via MCP for persistent memory (Streamable HTTP on Memara MCP host)',
    components: {
      modal: ClaudeDesktopIntegrationModal,
      successModal: ClaudeDesktopSuccessModal,
      configurationModal: ClaudeDesktopSuccessModal,
      landingPagePath: '/integrations/claude-desktop'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_config',
          label: 'Copy MCP Config',
          description: 'Copy MCP connector details or config snippets to your clipboard'
        },
        {
          id: 'download_instructions',
          label: 'Download Setup Instructions',
          description: 'Download setup instructions as markdown (Connectors + Streamable HTTP)'
        }
      ]
    },
    authRequirements: ['MCP Server Setup'],
    setupSteps: [
      'Create integration in Memara',
      'In Claude Desktop or claude.ai: Settings → Connectors → add custom connector',
      'Paste the per-space MCP URL from Memara (https://mcp.memara.io/c/{binding_id}/mcp) and complete OAuth',
      'On the OAuth approval page, select this integration',
      'Confirm Memara tools appear; restart Claude Desktop only if you use a local stdio config'
    ],
    metadata: {
      protocol: 'MCP',
      transportType: 'streamable-http',
      workingStatus: 'fully_operational'
    }
  },

  custom_gpt_action: {
    id: 'custom_gpt_action',
    name: 'Custom GPT Action',
    category: 'ai_assistants',
    socketTypes: ['api', 'mcp'],
    status: 'available',
    icon: 'openai',
    gradient: 'from-green-500 to-blue-600',
    description: 'Connect AI assistants with persistent memory capabilities via OpenAPI-compatible REST API',
    components: {
      modal: CustomGPTIntegrationModal,
      successModal: CustomGPTSuccessModal,
      configurationModal: CustomGPTSuccessModal,
      landingPagePath: '/integrations/chatgpt-custom-actions'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_openapi',
          label: 'Copy OpenAPI Spec',
          description: 'Copy OpenAPI specification to clipboard'
        },
        {
          id: 'download_openapi',
          label: 'Download OpenAPI File',
          description: 'Download OpenAPI specification as JSON file'
        },
        {
          id: 'copy_instructions',
          label: 'Copy GPT Instructions',
          description: 'Copy GPT system instructions to clipboard'
        }
      ]
    },
    authRequirements: ['OpenAPI Specification', 'API Key'],
    setupSteps: [
      'Create integration in Memara',
      'Copy OpenAPI specification',
      'Configure Custom GPT Actions',
      'Add authentication headers'
    ],
    metadata: {
      protocol: 'REST API',
      responseFormats: ['rich', 'simple'],
      rateLimit: 'configurable'
    }
  },

  // Communication Platform Integrations (Release 1.4)
  slack_bot: {
    id: 'slack_bot',
    name: 'Slack Bot',
    category: 'communication',
    socketTypes: ['bots', 'api', 'events', 'webhooks'],
    status: 'release_1_4',
    icon: 'slack',
    gradient: 'from-purple-600 to-pink-600',
    description: 'Professional workspace memory assistant with slash commands, mentions, and team collaboration',
    components: {
      landingPagePath: '/integrations/slack'
    },
    authRequirements: ['Slack App Installation', 'Workspace Permissions'],
    setupSteps: [
      'Install Memara Slack App',
      'Connect to workspace',
      'Configure bot permissions',
      'Start capturing team knowledge'
    ],
    metadata: {
      protocol: 'Slack Web API',
      supportedContent: ['messages', 'files', 'threads'],
      releaseVersion: '1.4'
    }
  },

  discord_bot: {
    id: 'discord_bot',
    name: 'Discord Bot',
    category: 'communication',
    socketTypes: ['bots', 'events', 'webhooks'],
    status: 'release_1_4',
    icon: 'discord',
    gradient: 'from-indigo-600 to-purple-600',
    description: 'Gaming community memory companion with command system and voice transcript support',
    components: {
      landingPagePath: '/integrations/discord'
    },
    authRequirements: ['Discord Bot Token', 'Server Permissions'],
    setupSteps: [
      'Add Memara bot to server',
      'Configure bot permissions',
      'Set up command prefixes',
      'Start saving gaming memories'
    ],
    metadata: {
      protocol: 'Discord API',
      supportedContent: ['messages', 'embeds', 'voice_transcripts'],
      releaseVersion: '1.4'
    }
  },

  microsoft_teams: {
    id: 'microsoft_teams',
    name: 'Microsoft Teams',
    category: 'communication',
    socketTypes: ['bots', 'events', 'webhooks'],
    status: 'release_1_4',
    icon: 'microsoftteams',
    gradient: 'from-blue-600 to-indigo-600',
    description: 'Enterprise knowledge capture with meeting transcripts and adaptive card interactions',
    components: {
      landingPagePath: '/integrations/microsoft-teams'
    },
    authRequirements: ['Microsoft App Registration', 'Teams Admin Consent'],
    setupSteps: [
      'Register Teams app',
      'Deploy to organization',
      'Configure meeting integration',
      'Enable knowledge capture'
    ],
    metadata: {
      protocol: 'Microsoft Graph API',
      supportedContent: ['messages', 'meetings', 'adaptive_cards'],
      releaseVersion: '1.4'
    }
  },

  telegram_bot: {
    id: 'telegram_bot',
    name: 'Telegram Bot',
    category: 'communication',
    socketTypes: ['bots', 'webhooks'],
    status: 'release_1_4',
    icon: 'telegram',
    gradient: 'from-blue-500 to-cyan-500',
    description: 'Personal memory assistant with voice transcription and location context support',
    components: {
      landingPagePath: '/integrations/telegram'
    },
    authRequirements: ['Telegram Bot Token'],
    setupSteps: [
      'Create bot with @BotFather',
      'Connect to Memara',
      'Configure personal preferences',
      'Start voice and text capture'
    ],
    metadata: {
      protocol: 'Telegram Bot API',
      supportedContent: ['text', 'voice', 'location', 'files'],
      releaseVersion: '1.4'
    }
  },

  // WhatsApp Integrations (Available)
  whatsapp_basic: {
    id: 'whatsapp_basic',
    name: 'WhatsApp Basic',
    category: 'communication',
    socketTypes: ['webhooks'],
    status: 'available',
    icon: "whatsapp",
    gradient: 'from-green-500 to-emerald-600',
    description: 'Transform your WhatsApp conversations into searchable memories that work across all your AI tools',
    components: {
      modal: WhatsAppIntegrationModal as any,
      configurationModal: WhatsAppIntegrationModal as any,
      landingPagePath: '/integrations/whatsapp-basic'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: true
    },
    authRequirements: ['WhatsApp Business Account', 'Twilio Integration'],
    setupSteps: [
      'Set up WhatsApp Business number',
      'Connect Twilio integration',
      'Configure message routing',
      'Start personal memory capture via WhatsApp'
    ],
    metadata: {
      protocol: 'Twilio WhatsApp API',
      tier: 'free',
      limitations: ['100 messages/month', '1 phone number'],
      mvpIntegration: true
    }
  },

  whatsapp_business: {
    id: 'whatsapp_business',
    name: 'WhatsApp Business',
    category: 'communication',
    socketTypes: ['webhooks', 'events'],
    status: 'coming_soon',
    icon: "whatsapp",
    gradient: 'from-green-600 to-teal-600',
    description: 'Advanced business intelligence with contact management and CRM integration',
    components: {
      landingPagePath: '/integrations/whatsapp-business'
    },
    authRequirements: ['WhatsApp Business API', 'Twilio Account'],
    setupSteps: [
      'Set up WhatsApp Business API',
      'Configure multi-line architecture',
      'Enable business intelligence',
      'Start customer interaction capture'
    ],
    metadata: {
      protocol: 'WhatsApp Business API',
      tier: 'pro',
      features: ['contact_intelligence', 'crm_integration', 'multiple_lines']
    }
  },

  // Automation Platform Integrations
  zapier: {
    id: 'zapier',
    name: 'Zapier',
    category: 'automation',
    socketTypes: ['api', 'events', 'webhooks'],
    status: 'available',
    icon: "zapier",
    gradient: 'from-orange-500 to-red-600',
    description: 'Automate memory workflows with 5000+ app integrations and smart triggers',
    components: {
      modal: ZapierIntegrationModal,
      successModal: ZapierSuccessModal,
      configurationModal: ZapierSuccessModal,
      landingPagePath: '/integrations/zapier'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_api_key',
          label: 'Copy API Key',
          description: 'Copy Zapier integration API key to clipboard'
        },
        {
          id: 'view_zapier_docs',
          label: 'View Zapier Documentation',
          description: 'Open Zapier platform documentation in new tab'
        }
      ]
    },
    authRequirements: ['Zapier Account', 'API Key'],
    setupSteps: [
      'Create integration and get API key',
      'Add credentials to your Zapier workflows',
      'Build Zaps with Memara memory operations',
      'Automate memory capture and retrieval'
    ],
    metadata: {
      protocol: 'REST API',
      supportedOperations: ['create', 'search', 'update', 'delete', 'list'],
      zapierApp: 'memara-integration',
      platformSupported: true,
      automationTypes: ['triggers', 'actions', 'searches'],
      deploymentTypes: ['zapier_cloud', 'zapier_developer']
    }
  },

  make_com: {
    id: 'make_com',
    name: 'Make.com',
    category: 'automation',
    socketTypes: ['api', 'events', 'webhooks'],
    status: 'in_progress',
    icon: "make",
    gradient: 'from-blue-500 to-purple-600',
    description: 'Visual automation builder with advanced logic and data transformation capabilities',
    components: {
      landingPagePath: '/integrations/make'
    },
    authRequirements: ['Make.com Account', 'API Access'],
    setupSteps: [
      'Connect Memara to Make.com',
      'Build visual automation flows',
      'Configure data transformations',
      'Deploy intelligent workflows'
    ],
    metadata: {
      protocol: 'Make.com API',
      supportedFeatures: ['visual_builder', 'data_transformation', 'conditional_logic'],
      developmentStatus: 'in_progress'
    }
  },

    n8n: {
    id: 'n8n',
    name: 'n8n',
    category: 'automation', 
    socketTypes: ['api'],
    status: 'available',
    icon: 'n8n',
    gradient: 'from-red-500 to-pink-600',
    description: 'Fair-code automation platform with 400+ integrations. Connect your entire tech stack to Memara using visual workflows.',
    components: {
      modal: N8NIntegrationModal,
      successModal: N8NSuccessModal,
      configurationModal: N8NSuccessModal,
      landingPagePath: '/integrations/n8n'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_api_key',
          label: 'Copy API Key',
          description: 'Copy n8n integration API key to clipboard'
        },
        {
          id: 'view_node_docs',
          label: 'View Node Documentation',
          description: 'Open n8n node documentation in new tab'
        }
      ]
    },
    authRequirements: ['n8n Instance', 'API Key'],
    setupSteps: [
      'Choose installation method (verified node, GUI, or manual)',
      'Install n8n-nodes-memara community node',
      'Add Memara credentials in n8n',
      'Create workflows with Memara operations',
      'Automate memory capture and retrieval'
    ],
    metadata: {
      protocol: 'REST API',
      supportedOperations: ['create', 'search', 'update', 'delete', 'list'],
      nodePackage: 'n8n-nodes-memara',
      npmPublished: true,
      installationMethods: [
        'verified_node', // Direct from n8n nodes panel
        'gui_install',   // Settings > Community Nodes
        'manual_install' // Command line/Docker
      ],
      deploymentTypes: ['n8n_cloud', 'self_hosted', 'docker']
    }
  },

  dify: {
    id: 'dify',
    name: 'Dify',
    category: 'automation',
    socketTypes: ['api'],
    status: 'available',
    icon: 'dify',
    gradient: 'from-indigo-500 to-violet-600',
    description:
      'LLM workflow and agent platform. Connect Dify apps to Memara with a dedicated API key and REST tools under /api/v1/dify.',
    components: {
      modal: DifyIntegrationModal,
      successModal: DifySuccessModal,
      configurationModal: DifySuccessModal,
      landingPagePath: '/integrations/dify'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_api_key',
          label: 'Copy API Key',
          description: 'Copy Dify integration API key to clipboard'
        },
        {
          id: 'view_dify_plugin_docs',
          label: 'Tool plugin docs',
          description: 'Open Dify tool plugin documentation'
        }
      ]
    },
    authRequirements: ['Dify workspace', 'Memara plugin', 'API Key'],
    setupSteps: [
      'Create a Dify integration in Memara (API socket)',
      'Copy the sk_dify_* API key',
      'Install the Memara Dify plugin and add provider credentials',
      'Use tools or scheduled workflows with list/search for polling-style automation'
    ],
    metadata: {
      protocol: 'REST API',
      supportedOperations: ['create', 'search', 'update', 'delete', 'list'],
      apiPrefix: '/api/v1/dify',
      pluginPackage: 'memara-dify-plugin'
    }
  },

  ifttt: {
    id: 'ifttt',
    name: 'IFTTT',
    category: 'automation',
    socketTypes: ['api', 'events', 'webhooks'],
    status: 'coming_soon',
    icon: "ifttt",
    gradient: 'from-cyan-500 to-blue-600',
    description: 'Connect everything to your memory with 700+ services and simple automation',
    components: {
      landingPagePath: '/integrations/ifttt'
    },
    authRequirements: ['IFTTT Account', 'Webhook URL'],
    setupSteps: [
      'Create IFTTT account',
      'Install Memara applet', 
      'Configure triggers and actions',
      'Start automated memory capture'
    ],
    metadata: {
      protocol: 'IFTTT Platform',
      supportedServices: 700,
      simplicity: 'beginner_friendly'
    }
  },

  // Productivity Platform Integrations (Coming Soon)
  notion: {
    id: 'notion',
    name: 'Notion',
    category: 'productivity',
    socketTypes: ['api'],
    status: 'coming_soon',
    icon: "notion",
    gradient: 'from-gray-700 to-gray-900',
    description: 'Sync memories with Notion databases and create intelligent knowledge bases',
    components: {
      landingPagePath: '/integrations/notion'
    },
    authRequirements: ['Notion Integration', 'Database Access'],
    setupSteps: [
      'Connect Notion workspace',
      'Select target databases',
      'Configure sync preferences',
      'Enable bidirectional updates'
    ],
    metadata: {
      protocol: 'Notion API',
      supportedContent: ['pages', 'databases', 'blocks'],
      syncDirection: 'bidirectional'
    }
  },

  google_workspace: {
    id: 'google_workspace',
    name: 'Google Workspace',
    category: 'productivity',
    socketTypes: ['api', 'webhooks'],
    status: 'coming_soon',
    icon: "google",
    gradient: 'from-blue-500 to-green-500',
    description: 'Transform productivity suite data into searchable business intelligence and personal insights',
    components: {
      landingPagePath: '/integrations/google-workspace'
    },
    authRequirements: ['Google Workspace Admin', 'OAuth 2.0', 'Service Account'],
    setupSteps: [
      'Set up Google Cloud project',
      'Configure OAuth consent screen',
      'Enable Workspace APIs',
      'Start capturing productivity data'
    ],
    metadata: {
      protocol: 'Google Workspace APIs',
      supportedApps: ['Gmail', 'Drive', 'Docs', 'Sheets', 'Calendar', 'Meet'],
      enterpriseFeatures: true
    }
  },

  // Development Platform Integrations (Coming Soon)
  github: {
    id: 'github',
    name: 'GitHub',
    category: 'development',
    socketTypes: ['api'],
    status: 'coming_soon',
    icon: "github",
    gradient: 'from-gray-700 to-black',
    description: 'Capture development insights, code reviews, and project discussions as memories',
    components: {
      landingPagePath: '/integrations/github'
    },
    authRequirements: ['GitHub Token', 'Repository Access'],
    setupSteps: [
      'Connect GitHub account',
      'Select repositories',
      'Configure capture preferences',
      'Start development memory capture'
    ],
    metadata: {
      protocol: 'GitHub API',
      supportedContent: ['issues', 'pull_requests', 'comments', 'commits'],
      privacyLevel: 'respects_repo_visibility'
    }
  },

  cursor: {
    id: 'cursor',
    name: 'Cursor',
    category: 'development',
    socketTypes: ['api', 'mcp'],
    status: 'available',
    icon: "cursor",
    gradient: 'from-blue-600 to-cyan-600',
    description: 'AI-powered code editor integration for capturing coding sessions and insights',
    components: {
      modal: CursorIntegrationModal as any,
      successModal: CursorSuccessModal,
      configurationModal: CursorSuccessModal,
      landingPagePath: '/integrations/cursor'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_cursor_config',
          label: 'Copy Cursor Config',
          description: 'Copy Cursor MCP configuration to clipboard'
        },
        {
          id: 'download_cursor_guide',
          label: 'Download Setup Guide',
          description: 'Download complete Cursor IDE setup instructions'
        }
      ]
    },
    authRequirements: ['OAuth Authorization', 'MCP Setup'],
    setupSteps: [
      'Create Cursor integration',
      'Set up Streamable HTTP MCP transport in Cursor',
      'Complete OAuth authorization flow',
      'Start coding with memory capture'
    ],
    metadata: {
      protocol: 'MCP',
      transportType: 'streamable-http',
      workingStatus: 'ready',
      supportedClients: ['cursor_ide', 'vscode_with_extension'],
      availableTools: [
        'store_memory',
        'search_memories',
        'list_memories',
        'get_memory',
        'update_memory',
        'delete_memory',
        'get_server_info',
      ],
      codingFeatures: {
        sessionCapture: true,
        aiInteractions: true,
        debuggingInsights: true,
        projectContext: true,
        languageSupport: 'all'
      }
    }
  },

  // Browser Integrations
  chrome_extension: {
    id: 'chrome_extension',
    name: 'Chrome Extension',
    category: 'browser',
    socketTypes: ['api'],
    status: 'available',
    icon: "googlechrome",
    gradient: 'from-blue-500 to-purple-500',
    description: 'Create custom Chrome extensions for capturing web content directly to your memory spaces',
    components: {
      modal: ChromeExtensionIntegrationModal,
      successModal: ChromeExtensionSuccessModal as any,
      extensionBuilderModal: ExtensionBuilderModal
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'download_extension',
          label: 'Download Extension',
          description: 'Download or re-download the Chrome extension'
        },
        {
          id: 'regenerate_extension',
          label: 'Regenerate Extension',
          description: 'Generate a new version of the Chrome extension'
        }
      ]
    },
    authRequirements: ['API Socket', 'Chrome Extension Builder'],
    setupSteps: [
      'Create Chrome extension integration',
      'Customize extension settings',
      'Generate and download extension',
      'Install extension in Chrome'
    ],
    metadata: {
      protocol: 'Chrome Extension API',
      supportedContent: ['articles', 'text_selections', 'bookmarks', 'web_pages'],
      privacyLevel: 'user_controlled',
      features: ['custom_branding', 'pre_configured', 'context_menu', 'toolbar_action']
    }
  },

  // Mobile Integrations (Concept)
  iphone_share_extension: {
    id: 'iphone_share_extension',
    name: 'iPhone Share Extension',
    category: 'mobile',
    socketTypes: ['api'],
    status: 'concept',
    icon: "ios",
    gradient: 'from-blue-500 to-purple-600',
    description: 'Quick memory capture from any iOS app using the native share sheet',
    components: {
    },
    authRequirements: ['iOS App', 'Share Extension Permission'],
    setupSteps: [
      'Install Memara iOS app',
      'Enable share extension',
      'Configure capture preferences',
      'Start mobile memory capture'
    ],
    metadata: {
      platform: 'iOS',
      supportedContent: ['text', 'images', 'links', 'locations'],
      offlineCapable: true
    }
  },

  // Social Media Integrations
  twitter_bookmarks: {
    id: 'twitter_bookmarks',
    name: 'X (formerly Twitter) Bookmarks',
    category: 'social_media',
    socketTypes: ['api'],
    status: 'available',
    icon: "twitter",
    gradient: 'from-blue-400 to-sky-600',
    description: 'Import your X bookmarks as searchable Memara memories with tweet text and metadata',
    components: {
      modal: XBookmarksIntegrationModal,
      successModal: XBookmarksSuccessModalContent as any,
      configurationModal: XBookmarksIntegrationModal as any,
      landingPagePath: '/integrations/x-bookmarks',
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: true,
    },
    authRequirements: ['X OAuth 2.0', 'bookmark.read scope'],
    setupSteps: [
      'Select API socket and sync interval',
      'Connect your X account via OAuth',
      'Historical bookmarks backfill automatically',
      'New bookmarks sync on your chosen schedule'
    ],
    metadata: {
      protocol: 'X API v2',
      supportedContent: ['tweets', 'user_metadata', 'hashtags'],
      syncFrequency: 'polling',
      mvpIntegration: true
    }
  },

  // 🚀 NEW MVP INTEGRATIONS - Core integrations required for MVP completion
  bare_api: {
    id: 'bare_api',
    name: 'REST API',
    category: 'development',
    socketTypes: ['api'],
    status: 'available',
    icon: "api",
    gradient: 'from-gray-500 to-slate-600',
    description: 'Direct REST API access with configurable endpoints and authentication',
    components: {
      modal: BareAPIIntegrationModal,
      successModal: BareAPISuccessModal,
      configurationModal: BareAPIConfigModal,
      landingPagePath: '/integrations/bare-api'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_config',
          label: 'Copy API Config',
          description: 'Copy API configuration to clipboard'
        },
        {
          id: 'test_connection',
          label: 'Test Connection',
          description: 'Test API endpoint connection'
        },
        {
          id: 'download_config',
          label: 'Download Config',
          description: 'Download API configuration as JSON'
        }
      ]
    },
    authRequirements: ['API Endpoint', 'Authentication Method'],
    setupSteps: [
      'Configure API endpoint URL',
      'Set authentication method (API key, Bearer token, etc.)',
      'Map response fields to memory structure',
      'Test connection and start using API'
    ],
    metadata: {
      protocol: 'REST API',
      supportedMethods: ['GET', 'POST'],
      authMethods: ['api_key', 'bearer_token', 'basic_auth', 'custom_headers'],
      responseMapping: true,
      mvpIntegration: true
    }
  },

  bare_mcp: {
    id: 'bare_mcp',
    name: 'Any MCP client',
    category: 'development',
    socketTypes: ['mcp'],
    status: 'available',
    icon: 'mcp',
    gradient: 'from-indigo-500 to-purple-600',
    description: 'Generic MCP server access for any MCP-compatible client with standard memory tools.',
    components: {
      modal: BareMCPIntegrationModal as any,
      successModal: BareMCPSuccessModal,
      configurationModal: BareMCPSuccessModal,
      landingPagePath: '/integrations/bare-mcp'
    },
    capabilities: {
      supportsConfiguration: true,
      supportsStatusMonitoring: false,
      customActions: [
        {
          id: 'copy_config',
          label: 'Copy MCP Config',
          description: 'Copy MCP server configuration to clipboard'
        },
        {
          id: 'download_setup_guide',
          label: 'Download Setup Guide',
          description: 'Download complete setup instructions for MCP clients'
        }
      ]
    },
    authRequirements: ['API Key', 'MCP Client'],
    setupSteps: [
      'Create bare MCP integration',
      'Copy MCP server configuration',
      'Add to your MCP client',
      'Test memory operations'
    ],
    metadata: {
      protocol: 'MCP',
      transportType: 'streamable-http',
      workingStatus: 'ready',
      supportedClients: ['claude_desktop', 'cursor', 'generic_mcp'],
      availableTools: [
        'store_memory',
        'search_memories',
        'list_memories',
        'get_memory',
        'update_memory',
        'delete_memory',
        'get_socket_info',
      ],
    }
  }
};

/**
 * Helper functions for integration registry
 */

export function getIntegration(id: string): IntegrationDefinition | undefined {
  return integrationRegistry[id];
}

/** Human-readable label for an integration `source` key (API / monitoring). */
function humanizeIntegrationSourceFallback(id: string): string {
  if (!id) return id;
  return id
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function getIntegrationDisplayName(source: string): string {
  return getIntegration(source)?.name ?? humanizeIntegrationSourceFallback(source);
}

export function getIntegrationsForSocketType(socketType: string): IntegrationDefinition[] {
  return Object.values(integrationRegistry).filter(integration => 
    integration.socketTypes.includes(socketType as any)
  );
}

export function getAvailableIntegrations(): IntegrationDefinition[] {
  return Object.values(integrationRegistry).filter(integration => 
    integration.status === 'available'
  );
}

export function getIntegrationsByCategory(category: string): IntegrationDefinition[] {
  return Object.values(integrationRegistry).filter(integration => 
    integration.category === category
  );
}

export function hasIntegration(id: string): boolean {
  return id in integrationRegistry;
}

export function getIntegrationIds(): string[] {
  return Object.keys(integrationRegistry);
}

export function getIntegrationCount(): number {
  return Object.keys(integrationRegistry).length;
} 